import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { State } from 'reducers';
import { DeviceCommandsSection } from './DeviceCommandsSection';
import { DeviceScriptSection } from './deviceScriptSection';
import { DeviceFilesSection } from './deviceFilesSection';
import { getDeviceDetailsRequest, deleteDeviceFileRequest, updateDeviceScript, publishDeviceScriptRequest } from 'actions/deviceActions';
import { getUploadsRequest } from 'actions/adminActions';
import { getUploads } from 'selectors/adminSelectors';
import { getBuildingThemes, HasDeviceId, HasUrlSlug } from 'selectors/buildingsSelectors';
import { getBuildingByUrlSlug, getBuildingDevice } from 'selectors/buildingsSelectors';
import { getDeviceType, getDeviceTypeNamesLookup } from 'selectors/deviceTypesSelectors';
import { getDeviceTypesRequest } from 'actions/deviceTypeActions';
import { defaultDeviceScript } from 'reducers/buildingsReducer';
import { useParams } from 'util/reactRouterHooks';

require('./deviceCommandsPage.css');

export const DeviceCommandsPage = () => {
    const { t } = useTranslation();

    const params = useParams<HasDeviceId & HasUrlSlug>();
    const { deviceId } = params;

    const device = useSelector((state: State) => getBuildingDevice(state, params));

    const deviceTypeId = device && device.deviceTypeId;
    const deviceType = useSelector((state: State) => getDeviceType(state, { deviceTypeId }));

    const groupName = (device && device.groupName) || t('DEVICES.NO_GROUP')!;

    // Note, have to get buildingId from urlSlug instead of device
    // because the device might not be loaded yet.
    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const buildingId = building && building.id;

    const script = (device && device.script) || null;
    const originalScript = (device && device.originalScript) || null;
        
    const deviceTypeNames = useSelector(getDeviceTypeNamesLookup);
    const lastCheckIn = (device && device.lastCheckIn) || null;
    const files = (device && device.files) || {};
    const hasChanges = script !== originalScript;
    const uploads = useSelector(getUploads);
    const uploadsLoaded = Object.keys(uploads).length > 0;
    const themes = useSelector((state: State) => getBuildingThemes(state, params) || {});

    const dispatch = useDispatch();

    const onChangeScript = useCallback((script: string) => {
        if (buildingId && deviceId) {
            dispatch(updateDeviceScript(buildingId, deviceId, script));
        }
    }, [ dispatch, buildingId, deviceId ]);

    const onClickPublishToDevice = useCallback(() => {
        if (buildingId && deviceId) {
            dispatch(publishDeviceScriptRequest(buildingId, deviceId, script, 'device'));
        }
    }, [ dispatch, buildingId, deviceId, script ]);

    const onClickPublishToGroup = useCallback(() => {
        if (buildingId && deviceId) {
            dispatch(publishDeviceScriptRequest(buildingId!, deviceId, script, 'group'));
        }
    }, [ dispatch, buildingId, deviceId, script ]);

    const onClickPublishToBuilding = useCallback(() => {
        if (buildingId && deviceId) {
            dispatch(publishDeviceScriptRequest(buildingId!, deviceId, script, 'building'));
        }
    }, [ dispatch, buildingId, deviceId, script ]);

    const onClickReset = useCallback(() => {
        if (buildingId && deviceId) {
            dispatch(updateDeviceScript(buildingId!, deviceId, defaultDeviceScript));
            dispatch(publishDeviceScriptRequest(buildingId!, deviceId, null, 'device'));
        }
    }, [ dispatch, buildingId, deviceId ]);

    const onClickDelete = useCallback((fileId: string) => {
        if (buildingId && deviceId) {
            dispatch(deleteDeviceFileRequest(buildingId, deviceId, fileId));
        }
    }, [ dispatch, buildingId, deviceId ]);
    
    useEffect(() => {
        // Load the device details
        if (buildingId && deviceId) {
            dispatch(getDeviceDetailsRequest(buildingId, deviceId));
            if (!uploadsLoaded) {
                dispatch(getUploadsRequest());
            }
        }

        // Load device types if we haven't already
        if (!Object.keys(deviceTypeNames).length) {
            dispatch(getDeviceTypesRequest());
        }
    }, [ dispatch, buildingId, deviceId, uploadsLoaded, deviceTypeNames ]);

    return (
        <div id="device-commands-page" className="page">
            <DeviceCommandsSection
                script={ script }
                onChangeScript={ onChangeScript }
                themes={ themes }
                uploads={ uploads }
                deviceType={ deviceType }
                disableRemoteUpdates={ building?.properties.remoteUpdateDisabled || false }
            />
            <DeviceScriptSection
                script={ script }
                readOnly={ building?.properties.remoteUpdateDisabled || false }
                groupName={ groupName }
                hasChanges={ hasChanges }
                lastCheckIn={ lastCheckIn }
                onChangeScript={ onChangeScript }
                onClickPublishToDevice={ onClickPublishToDevice }
                onClickPublishToGroup={ onClickPublishToGroup }
                onClickPublishToBuilding={ onClickPublishToBuilding }
                onClickReset={ onClickReset }
            />
            <DeviceFilesSection
                buildingId={ buildingId }
                deviceId={ deviceId! }
                files={ files }
                onClickDelete={ onClickDelete }
            />
        </div>
    );
};
